import { buildQueryRegistry, type QueryIssue, validateSearchRequest } from '@servicetim/domain';
import {
  PAGE_SIZE_DEFAULT,
  PAGE_SIZE_MAX,
  type SearchRequest,
  SearchRequestSchema,
  type ViewKind,
} from '@servicetim/shared';
import { Checkbox, Input, Label } from '@servicetim/ui/components';
import { useId, useMemo } from 'react';
import type { FieldCatalog } from '../../projects/catalog/field-catalog';
import { fieldOptions } from '../../projects/conditions/field-condition-row';
import { FormRow } from '../../projects/ui/form-row';
import { SimpleSelect } from '../../projects/ui/simple-select';
import { FilterBuilder } from './filter-builder';
import { canGroupBy, SortEditor } from './sort-editor';

export interface QueryBuilderProps {
  value: SearchRequest;
  onChange: (next: SearchRequest) => void;
  catalog: FieldCatalog;
  kind: ViewKind;
  idPrefix?: string;
}

export const emptyQuery = (): SearchRequest => ({ sort: [], page: { size: PAGE_SIZE_DEFAULT } });

/** A query scoped to one project (what a new project view starts with). */
export const projectQuery = (projectId: string): SearchRequest => ({
  filter: { and: [{ field: 'project_id', op: 'eq', value: projectId }] },
  sort: [{ field: 'updated_at', dir: 'desc' }],
  page: { size: PAGE_SIZE_DEFAULT },
});

/** Client-side check with the same validator the API runs (unknown fields, ops, `$me`, sort/group rules). */
export function validateQuery(query: SearchRequest, catalog: FieldCatalog): QueryIssue[] {
  const parsed = SearchRequestSchema.safeParse(query);
  if (!parsed.success) {
    return parsed.error.issues.map((i) => ({
      path: i.path.map(String).join('.'),
      code: 'query.bad_value',
      message: i.message,
    }));
  }
  const registry = buildQueryRegistry(
    catalog.custom.map((f) => ({
      key: f.key,
      type: f.type as Exclude<typeof f.type, 'text_search'>,
      archivedAt: f.archived ? 'archived' : null,
    })),
  );
  return validateSearchRequest(parsed.data, registry);
}

/** Filter tree, sort keys, group-by (boards) and page size for a saved view. */
export function QueryBuilder({ value, onChange, catalog, kind, idPrefix }: QueryBuilderProps) {
  const generated = useId();
  const id = idPrefix ?? generated;
  const groupOptions = useMemo(() => fieldOptions(catalog, canGroupBy), [catalog]);
  const grouped = value.groupBy !== undefined;
  return (
    <div className="space-y-5">
      <FormRow label="Filter">
        <FilterBuilder
          idPrefix={`${id}-filter`}
          value={value.filter}
          onChange={(filter) =>
            onChange(filter === undefined ? omitFilter(value) : { ...value, filter })
          }
          catalog={catalog}
        />
      </FormRow>
      <FormRow label="Sort" hint="Up to three keys; indexed custom fields only.">
        <SortEditor
          idPrefix={`${id}-sort`}
          value={value.sort}
          onChange={(sort) => onChange({ ...value, sort })}
          catalog={catalog}
        />
      </FormRow>
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm">
          <Checkbox
            id={`${id}-grouped`}
            checked={grouped}
            onCheckedChange={(next) =>
              onChange(
                next === true
                  ? { ...value, groupBy: { field: 'status_id', limitPerGroup: 20 } }
                  : omitGroupBy(value),
              )
            }
          />
          <Label htmlFor={`${id}-grouped`} className="font-normal">
            Group results {kind === 'board' ? '(board columns)' : ''}
          </Label>
        </div>
        {value.groupBy ? (
          <div className="flex flex-wrap items-end gap-3 pl-6">
            <FormRow label="Group by" htmlFor={`${id}-group`}>
              <SimpleSelect
                id={`${id}-group`}
                className="w-56"
                value={value.groupBy.field}
                options={groupOptions}
                onChange={(next) =>
                  next &&
                  value.groupBy &&
                  onChange({ ...value, groupBy: { ...value.groupBy, field: next } })
                }
              />
            </FormRow>
            <FormRow label="Per group" htmlFor={`${id}-limit`}>
              <Input
                id={`${id}-limit`}
                type="number"
                min={1}
                max={100}
                className="w-24"
                value={value.groupBy.limitPerGroup}
                onChange={(e) =>
                  value.groupBy &&
                  onChange({
                    ...value,
                    groupBy: {
                      ...value.groupBy,
                      limitPerGroup: clamp(Number(e.target.value) || 1, 1, 100),
                    },
                  })
                }
              />
            </FormRow>
          </div>
        ) : null}
      </div>
      <FormRow
        label="Page size"
        htmlFor={`${id}-page`}
        hint={`1-${PAGE_SIZE_MAX}; boards load one page per group.`}
        className="max-w-xs"
      >
        <Input
          id={`${id}-page`}
          type="number"
          min={1}
          max={PAGE_SIZE_MAX}
          className="w-24"
          value={value.page.size}
          onChange={(e) =>
            onChange({
              ...value,
              page: {
                ...value.page,
                size: clamp(Number(e.target.value) || PAGE_SIZE_DEFAULT, 1, PAGE_SIZE_MAX),
              },
            })
          }
        />
      </FormRow>
    </div>
  );
}

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, Math.round(n)));

function omitFilter(query: SearchRequest): SearchRequest {
  const { filter: _filter, ...rest } = query;
  return rest;
}

function omitGroupBy(query: SearchRequest): SearchRequest {
  const { groupBy: _groupBy, ...rest } = query;
  return rest;
}
